import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SiteDataContext = createContext(null);

// Default fallback data
const defaultCompanyInfo = {
  registeredName: 'FURD Ltd',
  tradingName: 'FURD Ltd',
  companyNumber: '10755319',
  vatNumber: '',
  addressLine1: 'Office 7, 35-37 Ludgate Hill',
  addressLine2: 'City of London',
  city: 'London',
  county: '',
  postcode: 'EC4M 7JN',
  country: 'United Kingdom',
  generalPhone: '020 7183 5251',
  generalEmail: 'info@furd.co.uk',
  linkedinUrl: '',
  founded: '2021',
  tagline: 'Building our future by engineering projects with generational value'
};

const defaultSettings = {
  logoUrl: 'https://customer-assets.emergentagent.com/job_furd-mep/artifacts/93z01v59_FURD.jpg',
  logoSizeDesktop: 120,
  logoSizeMobile: 80
};

export const SiteDataProvider = ({ children }) => {
  const [companyInfo, setCompanyInfo] = useState(defaultCompanyInfo);
  const [siteSettings, setSiteSettings] = useState(defaultSettings);
  const [accreditations, setAccreditations] = useState([]);
  const [contactSettings, setContactSettings] = useState({});
  const [services, setServices] = useState([]);
  const [servicesOverviewSettings, setServicesOverviewSettings] = useState({});
  const [sectors, setSectors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const fetchSiteData = useCallback(async (force = false) => {
    // Skip if recently fetched (within 10 seconds) unless forced
    if (!force && lastFetched && Date.now() - lastFetched < 10000) {
      return;
    }

    setFetchError(null);

    try {
      const [companyRes, settingsRes, accredRes, contactRes, servicesRes, servicesOverviewRes, sectorsRes, projectsRes, teamRes] = await Promise.all([
        axios.get(`${API}/company-info`).catch(e => ({ data: { companyInfo: {} }, error: e })),
        axios.get(`${API}/settings`).catch(e => ({ data: { settings: {} }, error: e })),
        axios.get(`${API}/accreditations`).catch(e => ({ data: { accreditations: [] }, error: e })),
        axios.get(`${API}/contact-settings`).catch(e => ({ data: { contactSettings: {} }, error: e })),
        axios.get(`${API}/services`).catch(e => ({ data: { services: [] }, error: e })),
        axios.get(`${API}/services-overview-settings`).catch(e => ({ data: { servicesOverviewSettings: {} }, error: e })),
        axios.get(`${API}/sectors`).catch(e => ({ data: { sectors: [] }, error: e })),
        axios.get(`${API}/projects`).catch(e => ({ data: { projects: [] }, error: e })),
        axios.get(`${API}/team`).catch(e => ({ data: { teamMembers: [] }, error: e }))
      ]);

      // Merge with defaults to ensure all fields exist
      const fetchedCompany = companyRes.data.companyInfo || {};
      setCompanyInfo({ ...defaultCompanyInfo, ...fetchedCompany });

      const fetchedSettings = settingsRes.data.settings || {};
      setSiteSettings({ ...defaultSettings, ...fetchedSettings });

      setAccreditations(accredRes.data.accreditations || []);
      setContactSettings(contactRes.data.contactSettings || {});
      setServices((servicesRes.data.services || []).filter(s => s.isVisible !== false));
      setServicesOverviewSettings(servicesOverviewRes.data.servicesOverviewSettings || {});
      setSectors((sectorsRes.data.sectors || []).filter(s => s.isVisible !== false).sort((a, b) => (a.order || 0) - (b.order || 0)));
      setProjects((projectsRes.data.projects || []).filter(p => p.isVisible !== false));
      setTeamMembers((teamRes.data.teamMembers || []).filter(t => t.isPublished !== false));
      
      setLastFetched(Date.now());
    } catch (error) {
      console.error('Error fetching site data:', error);
      setFetchError(error.message);
    } finally {
      setLoading(false);
    }
  }, [lastFetched]);

  useEffect(() => {
    fetchSiteData();
  }, []);

  // Helper to get full formatted address
  const getFormattedAddress = () => {
    const parts = [
      companyInfo.addressLine1,
      companyInfo.addressLine2,
      companyInfo.city,
      companyInfo.county,
      companyInfo.postcode,
      companyInfo.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Get active accreditations for a specific display location
  const getAccreditationsFor = (location) => {
    return accreditations.filter(acc => 
      acc.isActive !== false && 
      (acc.displayOn || []).includes(location)
    ).sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Get services by category
  const getServicesByCategory = (category) => {
    return services.filter(s => s.category === category).sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Get featured projects
  const getFeaturedProjects = (limit = 6) => {
    return projects
      .filter(p => p.isFeatured)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .slice(0, limit);
  };

  // Get featured team members
  const getFeaturedTeam = (limit = 4) => {
    return teamMembers
      .filter(t => t.isFeatured)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .slice(0, limit);
  };

  const value = {
    // Core data
    companyInfo,
    siteSettings,
    accreditations,
    contactSettings,
    services,
    servicesOverviewSettings,
    sectors,
    projects,
    teamMembers,
    
    // State
    loading,
    fetchError,
    refreshData: () => fetchSiteData(true),
    
    // Helpers
    getFormattedAddress,
    getAccreditationsFor,
    getServicesByCategory,
    getFeaturedProjects,
    getFeaturedTeam,
    
    // Computed properties for convenience
    phone: companyInfo.generalPhone,
    email: companyInfo.generalEmail,
    address: getFormattedAddress(),
    logo: siteSettings.logoUrl || defaultSettings.logoUrl,
    companyName: companyInfo.tradingName || companyInfo.registeredName || 'FURD Ltd',
    
    // Design and consultancy services for homepage
    designServices: services.filter(s => s.category === 'design'),
    consultancyServices: services.filter(s => s.category === 'consultancy')
  };

  return (
    <SiteDataContext.Provider value={value}>
      {children}
    </SiteDataContext.Provider>
  );
};

export const useSiteData = () => {
  const context = useContext(SiteDataContext);
  if (!context) {
    throw new Error('useSiteData must be used within a SiteDataProvider');
  }
  return context;
};

export default SiteDataContext;
